import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoChevronBack, IoEye, IoEyeOff, IoLockClosed } from "react-icons/io5";

import { setWebPassword } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { assetPath } from "../utils/assets";
import { isTelegramMiniApp } from "../utils/authSession";

const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 20;

const SetPassword = ({ mode = "set" }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isChangePassword = mode === "change";

    const [form, setForm] = useState({
        password: "",
        confirmPassword: "",
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    if (!token) {
        return <Navigate to="/login" replace />;
    }

    if (isChangePassword && isTelegramMiniApp()) {
        return <Navigate to="/" replace />;
    }

    const handleChange = (key) => (event) => {
        setForm((prev) => ({
            ...prev,
            [key]: event.target.value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setErrorMessage("");

        try {
            const password = form.password || "";
            const confirmPassword = form.confirmPassword || "";

            if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH) {
                setErrorMessage(t("invalidPasswordLength", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }));
                return;
            }

            if (password !== confirmPassword) {
                setErrorMessage(t("passwordsDoNotMatch"));
                return;
            }

            const response = await setWebPassword({
                password,
                confirmPassword,
            });

            if (response.success) {
                navigate("/", { replace: true });
            } else {
                setErrorMessage(response.data || t("pleaseTryLater"));
            }
        } catch (error) {
            setErrorMessage(t("pleaseTryLater"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="relative min-h-screen overflow-hidden bg-[#031523] text-white">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(17,155,255,0.2)_0%,_transparent_34%),linear-gradient(180deg,_#07253A_0%,_#031523_42%,_#02111A_100%)]" />
                <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#00D9FF]/10 blur-3xl" />
            </div>

            <div className="relative mx-auto flex min-h-screen w-full max-w-7xl items-center justify-center px-4 py-8 lg:px-8">
                <div className="w-full max-w-[460px]">
                    <div className="mb-5 flex items-center justify-between">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white"
                        >
                            <IoChevronBack className="text-sm" />
                            <span>{t("backToLobby")}</span>
                        </Link>

                        <img src={assetPath("/icons/logo.png")} alt="Indo368" className="w-[128px]" />
                    </div>

                    <div className="rounded-[2rem] bg-[#082032]/92 p-6 shadow-[0_32px_90px_-54px_rgba(0,0,0,0.9)] ring-1 ring-[#0E4F77] backdrop-blur-xl sm:p-7">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.28em] text-[#84C9E5]/70">
                                {isChangePassword ? t("changePassword", "Change Password") : t("setPassword")}
                            </p>
                            <h1 className="mt-3 text-3xl font-black text-white">
                                {isChangePassword ? t("changePasswordTitle", "Change your password") : t("setPasswordTitle")}
                            </h1>
                            <p className="mt-3 text-sm leading-6 text-white/68">
                                {isChangePassword
                                    ? t("changePasswordDescription", "Enter a new password to replace the temporary password sent to you.")
                                    : t("setPasswordDescription")}
                            </p>
                            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#84C9E5]/72">
                                {isChangePassword
                                    ? t("changePasswordNotice", "Use this password for your next website login.")
                                    : t("setPasswordOnce")}
                            </p>
                        </div>

                        {errorMessage && (
                            <div className="mt-5 rounded-[1.2rem] bg-[#3A1318] px-4 py-3 text-sm text-white ring-1 ring-[#7E2D37]">
                                {errorMessage}
                            </div>
                        )}

                        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                            <label className="block" htmlFor="set-password">
                                <span className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#84C9E5]/72">
                                    <IoLockClosed className="text-sm" />
                                    {t("newPassword")}
                                </span>
                                <div className="flex items-center rounded-[1.3rem] bg-[#061A2A] px-4 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF]">
                                    <input
                                        id="set-password"
                                        type={showPassword ? "text" : "password"}
                                        minLength={PASSWORD_MIN_LENGTH}
                                        maxLength={PASSWORD_MAX_LENGTH}
                                        value={form.password}
                                        onChange={handleChange("password")}
                                        className="h-14 w-full bg-transparent text-base font-medium text-white outline-none placeholder:text-white/30"
                                        placeholder={t("enterNewPassword")}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="text-white/55 transition-colors duration-300 hover:text-white"
                                    >
                                        {showPassword ? <IoEyeOff className="text-lg" /> : <IoEye className="text-lg" />}
                                    </button>
                                </div>
                            </label>

                            <label className="block" htmlFor="confirm-password">
                                <span className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#84C9E5]/72">
                                    <IoLockClosed className="text-sm" />
                                    {t("confirmPassword")}
                                </span>
                                <div className="flex items-center rounded-[1.3rem] bg-[#061A2A] px-4 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF]">
                                    <input
                                        id="confirm-password"
                                        type={showConfirmPassword ? "text" : "password"}
                                        minLength={PASSWORD_MIN_LENGTH}
                                        maxLength={PASSWORD_MAX_LENGTH}
                                        value={form.confirmPassword}
                                        onChange={handleChange("confirmPassword")}
                                        className="h-14 w-full bg-transparent text-base font-medium text-white outline-none placeholder:text-white/30"
                                        placeholder={t("confirmCreatePassword")}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        className="text-white/55 transition-colors duration-300 hover:text-white"
                                    >
                                        {showConfirmPassword ? <IoEyeOff className="text-lg" /> : <IoEye className="text-lg" />}
                                    </button>
                                </div>
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_38%,_#00B8EE_100%)] px-6 py-3.5 text-sm font-black uppercase tracking-[0.18em] text-slate-950 shadow-[0_20px_40px_-28px_rgba(0,151,201,1)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitting ? t("saving") : t("savePassword")}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SetPassword;